import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { EnvelopeIcon, ChatCircleTextIcon, BellIcon } from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import EmptyState from "../../components/EmptyState";
import { ErrorBanner } from "../../components/crm/Form";
import NotificationStatusBadge from "../../components/notifications/NotificationStatusBadge";
import api, { formatApiError } from "../../lib/api";

const EVENT_LABEL = {
  billing_past_due: "Billing past due",
  billing_unpaid: "Billing unpaid",
  ack_received: "Owner acknowledged",
  ack_requested: "Reminder sent",
};

const EVENT_FILTERS = [
  { value: "", label: "All events" },
  { value: "billing_past_due", label: "Past due" },
  { value: "billing_unpaid", label: "Unpaid" },
  { value: "ack_received", label: "Ack received" },
  { value: "ack_requested", label: "Ack requested" },
];

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "sent", label: "Sent" },
  { value: "failed", label: "Failed" },
  { value: "stubbed_no_provider", label: "Stub" },
];

export default function NotificationsList() {
  const [params, setParams] = useSearchParams();
  const eventType = params.get("event_type") || "";
  const status = params.get("status") || "";
  const subId = params.get("related_subscription_id") || "";
  const reportId = params.get("related_report_id") || "";

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [err, setErr] = useState("");
  const [retryingId, setRetryingId] = useState(null);

  const load = useCallback(async () => {
    setErr("");
    const qs = new URLSearchParams();
    if (eventType) qs.set("event_type", eventType);
    if (status) qs.set("status", status);
    if (subId) qs.set("related_subscription_id", subId);
    if (reportId) qs.set("related_report_id", reportId);
    qs.set("limit", "50");
    try {
      const r = await api.get(`/notifications/?${qs.toString()}`);
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, [eventType, status, subId, reportId]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next);
  };

  const retry = async (id) => {
    setRetryingId(id);
    try {
      await api.post(`/notifications/${id}/retry`);
      toast.success("Retry attempted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <EntityHeader
          title="Notifications"
          subtitle="Outbound emails for billing alerts and report acknowledgments"
        />
        <ErrorBanner message={err} />

        <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mr-1">
              Event
            </span>
            {EVENT_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                data-testid={`notif-filter-event-${f.value || "all"}`}
                onClick={() => setFilter("event_type", f.value)}
                className={`text-xs px-2.5 py-1 rounded-md border transition ${
                  eventType === f.value
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mr-1">
              Status
            </span>
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                data-testid={`notif-filter-status-${f.value || "all"}`}
                onClick={() => setFilter("status", f.value)}
                className={`text-xs px-2.5 py-1 rounded-md border transition ${
                  status === f.value
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {(subId || reportId) && (
            <div className="text-[11px] text-slate-500">
              Filtered to{" "}
              {subId ? (
                <>
                  subscription{" "}
                  <code className="font-data text-slate-700">{subId.slice(0, 8)}…</code>
                </>
              ) : (
                <>
                  report{" "}
                  <code className="font-data text-slate-700">{reportId.slice(0, 8)}…</code>
                </>
              )}{" "}
              ·{" "}
              <button
                onClick={() => {
                  const next = new URLSearchParams(params);
                  next.delete("related_subscription_id");
                  next.delete("related_report_id");
                  setParams(next);
                }}
                className="font-semibold text-slate-700 hover:text-slate-900 underline"
              >
                clear
              </button>
            </div>
          )}
          <div className="text-[11px] text-slate-500 pt-1">
            Showing {items.length} of {total}
          </div>
        </div>

        <div
          className="bg-white border border-slate-200 rounded-md overflow-hidden"
          data-testid="notif-list"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                <th className="px-4 py-2.5">When</th>
                <th className="px-4 py-2.5">Event</th>
                <th className="px-4 py-2.5">Subject</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Recipient</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <EmptyState
                  inTable
                  colSpan={6}
                  icon={BellIcon}
                  title={
                    eventType || status || subId || reportId
                      ? "No matching notifications"
                      : "No notifications yet"
                  }
                  description={
                    eventType || status || subId || reportId
                      ? "Try clearing the filters above."
                      : "Outbound emails and SMS messages will appear here whenever billing alerts or report acknowledgments are sent."
                  }
                  testId="notif-empty"
                />
              )}
              {items.map((n) => (
                <tr
                  key={n.id}
                  data-testid={`notif-row-${n.id}`}
                  className="border-t border-slate-200 hover:bg-slate-50/50"
                >
                  <td className="px-4 py-2 align-top text-xs text-slate-600">
                    {new Date(n.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 align-top">
                    <span className="inline-flex items-center gap-1.5">
                      {n.channel === "sms" ? (
                        <ChatCircleTextIcon
                          size={14}
                          weight="duotone"
                          className="text-emerald-600"
                          title="SMS"
                          data-testid={`notif-channel-sms-${n.id}`}
                        />
                      ) : (
                        <EnvelopeIcon
                          size={14}
                          weight="regular"
                          className="text-slate-500"
                          title="Email"
                          data-testid={`notif-channel-email-${n.id}`}
                        />
                      )}
                      <span className="text-xs font-semibold text-slate-900">
                        {EVENT_LABEL[n.event_type] || n.event_type}
                      </span>
                    </span>
                  </td>
                  <td className="px-4 py-2 align-top">
                    <Link
                      to={`/notifications/${n.id}`}
                      className="text-sm text-slate-900 hover:underline"
                    >
                      {n.channel === "sms"
                        ? (n.body_sms || "").slice(0, 60) +
                          ((n.body_sms || "").length > 60 ? "…" : "")
                        : n.subject}
                    </Link>
                  </td>
                  <td className="px-4 py-2 align-top">
                    <NotificationStatusBadge status={n.status} />
                  </td>
                  <td className="px-4 py-2 align-top text-xs text-slate-700">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mr-1">
                      {n.recipient_kind === "internal_users" ? "internal" : "customer"}
                    </span>
                    {n.channel === "sms"
                      ? (n.recipient_phones || []).slice(0, 2).join(", ")
                      : (n.recipient_emails || []).slice(0, 2).join(", ")}
                    {n.channel === "sms"
                      ? (n.recipient_phones && n.recipient_phones.length > 2 && (
                          <span> +{n.recipient_phones.length - 2}</span>
                        ))
                      : (n.recipient_emails && n.recipient_emails.length > 2 && (
                          <span> +{n.recipient_emails.length - 2}</span>
                        ))}
                  </td>
                  <td className="px-4 py-2 align-top text-right">
                    {(n.status === "failed" || n.status === "stubbed_no_provider") && (
                      <button
                        type="button"
                        data-testid={`notif-retry-${n.id}`}
                        onClick={() => retry(n.id)}
                        disabled={retryingId === n.id}
                        className="text-xs font-semibold text-slate-700 hover:text-slate-900 disabled:opacity-50"
                      >
                        {retryingId === n.id ? "Retrying…" : "Retry"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
